import React, { useMemo, memo } from "react";
import cx from "classnames";
import type { IMessage, StopType } from "@typing/wsui/getMessages";
import type { ToolClickHandler } from "../../types";
import ThinkingProcessV2, { type IThinkReference } from "../ThinkingProcessV2";
import MarkdownContent from "../MarkdownContent";
import AppendAsk from "../AppendAsk";
import FileCard, { type FileCardContent } from "../FileCard";
import MediaList, { type MediaListContent } from "../MediaList";
import styles from "./index.module.less";

const parseFileCardContent = (content: string): FileCardContent | null => {
    try {
        return JSON.parse(content) as FileCardContent;
    } catch {
        return null;
    }
};

const parseMediaListContent = (content: string): MediaListContent | null => {
    try {
        return JSON.parse(content) as MediaListContent;
    } catch {
        return null;
    }
};

interface IAIMessageProps {
    message: IMessage;
    expandedThinking: Record<string, boolean>;
    onToggleThinking: (segmentKey: string) => void;
    onToolClick: ToolClickHandler;
    sessionId: string;
    onAppendAskClick: (question: string) => void;
    sseLoading?: boolean;
}

/**
 * AI 消息组件
 */
const AIMessage: React.FC<IAIMessageProps> = ({
    message,
    expandedThinking,
    onToggleThinking,
    onToolClick,
    sessionId,
    onAppendAskClick,
    sseLoading,
}) => {
    // 解析当前消息中的引用文献列表，供 MarkdownContent 查找 reference_id
    const thinkReferences = useMemo<IThinkReference[] | undefined>(() => {
        for (const section of message.sections) {
            // 优先使用 oeReferences（包含完整 feature/tool_tip），fallback 到 thinkProcessV2
            const oeSeg = section.segments.find((s) => s.type === "oeReferences");
            const seg = oeSeg || section.segments.find((s) => s.type === "thinkProcessV2");
            if (seg) {
                try {
                    return JSON.parse(seg.content).references;
                } catch {
                    return undefined;
                }
            }
        }
        return undefined;
    }, [message.sections]);

    // 判断是否需要在已有 sections 之后显示 loading（section 间等待状态）
    const showTailLoading = message.isLoading && message.isHold === true;

    if (message.isLoading && message.sections.length === 0) {
        return (
            <div className={styles.aiBubble}>
                <div className={styles.loadingPlaceholder}>
                    <div className={styles.loadingDots}>
                        <span className={styles.dot} />
                        <span className={styles.dot} />
                        <span className={styles.dot} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {message.sections.map((section, sectionIdx) => {
                const isLastSection = sectionIdx === message.sections.length - 1;
                const isOE = section.segments.some((s) => s.type === 'thinkProcessV2' || s.type === 'oeReferences');
                const contentSegments = section.segments.filter((s) => s.type !== 'appendAsk');
                const actionSegments = section.segments.filter((s) => s.type === 'appendAsk');
                const isFileCardOnly = contentSegments.length === 1 && contentSegments[0].type === 'fileCard';
                const hasEmbeddedFileCard = !isFileCardOnly && contentSegments.some((s) => s.type === 'fileCard');
                const isMediaListOnly = contentSegments.length === 1 && contentSegments[0].type === 'mediaList';

                return (
                    <div
                        key={section.section_idx}
                        className={cx(styles.aiBubble, {
                            [styles.fullWidth]: isOE,
                            [styles.mediaBubble]: isMediaListOnly,
                            [styles.fileCardOnlyBubble]: isFileCardOnly,
                            [styles.embeddedFileCardBubble]: hasEmbeddedFileCard,
                        })}
                    >
                        {contentSegments.map((seg, idx) => {
                            const prevSeg = contentSegments[idx - 1];
                            const needDivider =
                                idx > 0 && prevSeg?.type === "thinkProcessV2";
                            const isLast =
                                isLastSection && idx === contentSegments.length - 1 && actionSegments.length === 0;
                            const segmentKey = `${message.message_id}_${seg.segment_idx}`;
                            const stopType: StopType | undefined = isLast ? message.stop_type : undefined;

                            return (
                                <React.Fragment key={seg.segment_idx}>
                                    {needDivider && <div className={styles.divider} />}
                                    {seg.type === "thinkProcessV2" && (
                                        <ThinkingProcessV2
                                            segment={seg}
                                            sessionId={sessionId}
                                            messageId={message.message_id}
                                            isExpanded={expandedThinking[segmentKey] === true}
                                            onToggleExpand={() => onToggleThinking(segmentKey)}
                                            stopType={stopType}
                                        />
                                    )}
                                    {seg.type === 'oeReferences' && null}
                                    {seg.type === 'fileCard' &&
                                        (() => {
                                            const content = parseFileCardContent(seg.content);
                                            if (!content) return null;
                                            const fileCard = <FileCard content={content} />;
                                            return hasEmbeddedFileCard ? (
                                                <div className={styles.embeddedFileCard}>{fileCard}</div>
                                            ) : fileCard;
                                        })()}
                                    {seg.type === 'mediaList' &&
                                        (() => {
                                            const content = parseMediaListContent(seg.content);
                                            return content ? <MediaList media={content} /> : null;
                                        })()}
                                    {seg.type === 'markdown' && (
                                        <MarkdownContent
                                            segment={seg}
                                            messageId={message.message_id}
                                            sessionId={sessionId}
                                            onToolClick={onToolClick}
                                            stopType={stopType}
                                            references={thinkReferences}
                                            sseLoading={sseLoading}
                                        />
                                    )}
                                </React.Fragment>
                            );
                        })}

                        {isLastSection && message.stop_type && !contentSegments.some((s) => s.type === "markdown") && (
                            <div className={styles.stopNotice}>回答已终止</div>
                        )}

                        {isOE && actionSegments.length > 0 && (
                            <>
                                <div className={styles.divider} />
                                {actionSegments.map((seg) => (
                                    <AppendAsk
                                        key={seg.segment_idx}
                                        segment={seg}
                                        messageId={message.message_id}
                                        sessionId={sessionId}
                                        onAppendAskClick={onAppendAskClick}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                );
            })}
            {showTailLoading && (
                <div className={styles.aiBubble}>
                    <div className={styles.loadingPlaceholder}>
                        <div className={styles.loadingDots}>
                            <span className={styles.dot} />
                            <span className={styles.dot} />
                            <span className={styles.dot} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default memo(AIMessage);
